import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Shield, ChevronLeft, ChevronRight } from "lucide-react";
import { useAgentIssues } from "@/hooks/useAgentDetail";
import { safeFormatDistanceToNow } from "@/utils/dateUtils";
import type { AgentIssuesParams } from "@/services/aiAgentsService";

interface IssuesTabProps {
  agentId: number;
}

const SEVERITY_COLORS: Record<string, string> = {
  CRITICAL: "bg-red-100 text-red-800 border-red-200",
  HIGH: "bg-orange-100 text-orange-800 border-orange-200",
  MEDIUM: "bg-yellow-100 text-yellow-800 border-yellow-200",
  LOW: "bg-gray-100 text-gray-600 border-gray-200",
  INFO: "bg-blue-100 text-blue-600 border-blue-200",
};

const STATUS_COLORS: Record<string, string> = {
  OPEN: "bg-red-50 text-red-700 border-red-200",
  IN_REVIEW: "bg-yellow-50 text-yellow-700 border-yellow-200",
  RESOLVED: "bg-green-50 text-green-700 border-green-200",
  FALSE_POSITIVE: "bg-gray-50 text-gray-600 border-gray-200",
  SUPPRESSED: "bg-purple-50 text-purple-600 border-purple-200",
};

const ITEMS_PER_PAGE = 20;

export function IssuesTab({ agentId }: IssuesTabProps) {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [severityFilter, setSeverityFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [typeFilter] = useState<string>("all");

  const params: AgentIssuesParams = useMemo(
    () => ({
      page,
      items_per_page: ITEMS_PER_PAGE,
      severity: severityFilter !== "all" ? severityFilter : undefined,
      status: statusFilter !== "all" ? statusFilter : undefined,
      issue_type: typeFilter !== "all" ? typeFilter : undefined,
    }),
    [page, severityFilter, statusFilter, typeFilter]
  );

  const { data: response, isLoading, error } = useAgentIssues(agentId, params);

  const issues = response?.data ?? [];
  const totalCount = response?.total_count ?? 0;
  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE) || 1;

  const handleFilterChange = () => {
    setPage(1);
  };

  return (
    <div className="space-y-4 pt-6">
      {/* Filter bar */}
      <div className="flex items-center gap-3 flex-wrap">
        <Select
          value={severityFilter}
          onValueChange={(v) => {
            setSeverityFilter(v);
            handleFilterChange();
          }}
        >
          <SelectTrigger className="w-[140px] rounded-full bg-white">
            <SelectValue placeholder="Severity" />
          </SelectTrigger>
          <SelectContent className="rounded-[20px] bg-white">
            <SelectItem value="all">All Severities</SelectItem>
            <SelectItem value="CRITICAL">Critical</SelectItem>
            <SelectItem value="HIGH">High</SelectItem>
            <SelectItem value="MEDIUM">Medium</SelectItem>
            <SelectItem value="LOW">Low</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={statusFilter}
          onValueChange={(v) => {
            setStatusFilter(v);
            handleFilterChange();
          }}
        >
          <SelectTrigger className="w-[140px] rounded-full bg-white">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent className="rounded-[20px] bg-white">
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="OPEN">Open</SelectItem>
            <SelectItem value="IN_REVIEW">In Review</SelectItem>
            <SelectItem value="RESOLVED">Resolved</SelectItem>
            <SelectItem value="FALSE_POSITIVE">False Positive</SelectItem>
            <SelectItem value="SUPPRESSED">Suppressed</SelectItem>
          </SelectContent>
        </Select>

        <div className="ml-auto text-sm text-muted-foreground">
          {totalCount} issue{totalCount !== 1 ? "s" : ""}
        </div>
      </div>

      {/* Issues table */}
      {isLoading && (
        <div className="space-y-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-12 w-full rounded-[10px]" />
          ))}
        </div>
      )}

      {error && (
        <div className="text-center py-8 text-destructive">
          <p className="text-sm">Failed to load issues.</p>
        </div>
      )}

      {!isLoading && !error && issues.length === 0 && (
        <div className="text-center py-12 text-muted-foreground">
          <Shield className="h-10 w-10 mx-auto mb-3 opacity-50" />
          <p className="text-sm">No security issues found for this agent.</p>
        </div>
      )}

      {!isLoading && !error && issues.length > 0 && (
        <>
          <div className="overflow-hidden rounded-[15px] border bg-white">
            <Table>
              <TableHeader>
                <TableRow className="h-9">
                  <TableHead className="py-2 text-xs font-medium">Title</TableHead>
                  <TableHead className="py-2 text-xs font-medium w-[100px]">Severity</TableHead>
                  <TableHead className="py-2 text-xs font-medium w-[100px]">Status</TableHead>
                  <TableHead className="py-2 text-xs font-medium w-[100px] text-right">Risk Score</TableHead>
                  <TableHead className="py-2 text-xs font-medium w-[140px]">Detected</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {issues.map((issue) => (
                  <TableRow
                    key={issue.id}
                    className="cursor-pointer hover:bg-muted/50 transition-colors"
                    onClick={() => navigate(`/issues/SEC-${String(issue.id).padStart(3, "0")}`)}
                    tabIndex={0}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") navigate(`/issues/SEC-${String(issue.id).padStart(3, "0")}`);
                    }}
                  >
                    <TableCell className="py-3">
                      <span className="text-sm font-medium">{issue.title}</span>
                    </TableCell>
                    <TableCell className="py-3">
                      <Badge className={`rounded-[5px] text-xs ${SEVERITY_COLORS[issue.severity] ?? ""}`}>
                        {issue.severity}
                      </Badge>
                    </TableCell>
                    <TableCell className="py-3">
                      <Badge variant="outline" className={`rounded-[5px] text-xs ${STATUS_COLORS[issue.status] ?? ""}`}>
                        {issue.status.replace("_", " ")}
                      </Badge>
                    </TableCell>
                    <TableCell className="py-3 text-right">
                      <span className="text-sm font-mono">{(issue.risk_score * 100).toFixed(0)}%</span>
                    </TableCell>
                    <TableCell className="py-3">
                      <span className="text-xs text-muted-foreground">
                        {safeFormatDistanceToNow(issue.detected_at, { addSuffix: true })}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">
                Page {page} of {totalPages}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-full"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-full"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
